import { DynamicFieldData } from '@/shared/components/DynamicForm/models/DynamicFieldData'
import { DynamicFormDefinition } from '@/shared/components/DynamicForm/models/DynamicFormSection'
import { FormFieldDefaultValue, FormFieldSelectOptions, getDynamicFormFields } from '@/shared/lib/getDynamicFormFields'

const defaultWebAdminDetailFields: DynamicFieldData[] = [
  {
    fieldType: 'select',
    fieldName: 'roleId',
    label: 'Role',
    placeholder: 'Select Role for this Web Admin',
    defaultValue: undefined,
    required: true,
    options: [],
    config: {
      required: 'Role is required',
    },
  },
  {
    fieldType: 'email',
    fieldName: 'login',
    label: 'Login',
    placeholder: 'Enter Login (Email)',
    defaultValue: '',
    required: true,
    config: {
      required: 'Login is required',
    },
  },
  {
    fieldType: 'text',
    fieldName: 'name',
    label: 'Name',
    placeholder: 'Enter Web Admin Name',
    defaultValue: '',
    required: true,
    config: {
      required: 'Name is required',
    },
  },
  {
    fieldType: 'multiselect',
    fieldName: 'groupIds',
    label: 'Groups',
    placeholder: 'Select Groups this Web Admin has access to',
    defaultValue: [],
    options: [],
    required: true,
    config: {
      required: 'Please select at least one group',
    },
  },
]

const passwordFields: DynamicFieldData[] = [
  {
    fieldType: 'password',
    fieldName: 'password',
    label: 'Password',
    placeholder: 'Enter Password',
    defaultValue: '',
    required: true,
    config: {
      required: 'Password is required',
      minLength: {
        value: 8,
        message: 'Password must be at least 8 characters',
      },
    },
  },
  {
    fieldType: 'password',
    fieldName: 'confirmPassword',
    label: 'Confirm Password',
    placeholder: 'Re-Enter Password',
    defaultValue: '',
    required: true,
    config: {
      required: 'Password confirmation is required',
      minLength: {
        value: 8,
        message: 'Password must be at least 8 characters',
      },
      validate: (value: string, formValues: Record<string, unknown>) => 
        value === formValues.password || 'Passwords do not match',
    },
  },
]

export const initialWebAdminFormValues = {
  login: '',
  name: '',
  roleId: undefined,
  groupIds: [],
  isActive: true,
}

type GetWebAdminFormDefinitionArgs = {
  defaultValues?: FormFieldDefaultValue
  selectOptions?: FormFieldSelectOptions
  isResetPassword: boolean
  showPasswordFields: boolean
  isReadOnly: boolean
}

export const getWebAdminFormDefinition = ({
  defaultValues,
  selectOptions,
  isResetPassword = false,
  showPasswordFields = false,
  isReadOnly = false,
}: GetWebAdminFormDefinitionArgs): DynamicFormDefinition => {
  // When resetting password, disable all non-password fields
  const adminFields = defaultWebAdminDetailFields.map(field => ({
    ...field,
    disabled: isReadOnly || isResetPassword,
  }))

  const fields = getDynamicFormFields({
    fields: [
      ...adminFields,
      ...((isResetPassword || showPasswordFields) ? passwordFields : []),
    ],
    defaultValues,
    selectOptions,
  })

  const formDefinition: DynamicFormDefinition = {
    formType: 'default',
    sections: [
      {
        label: 'Web Admin Details',
        fields,
      },
    ],
  }

  return formDefinition
}